// Blockchain Data Fetching Service for Real Scam Address Scanning
import { addressService, transactionService } from './investigationService';

// Bitcoin API Service
const bitcoinAPI = {
  async getAddressInfo(address) {
    try {
      // Using BlockCypher API for Bitcoin data
      const response = await fetch(`https://api.blockcypher.com/v1/btc/main/addrs/${address}/balance`);
      if (!response?.ok) throw new Error('Bitcoin API request failed');
      
      const data = await response?.json();
      return {
        address,
        network: 'bitcoin',
        balance: (data?.balance || 0) / 100000000, // Convert satoshi to BTC
        transaction_count: data?.n_tx || 0,
        first_activity: data?.tx_refs?.[0]?.confirmed,
        last_activity: data?.tx_refs?.[data?.tx_refs?.length - 1]?.confirmed
      };
    } catch (error) {
      throw new Error(`Bitcoin API error: ${error?.message}`);
    }
  },

  async getAddressTransactions(address, limit = 50) {
    try {
      const response = await fetch(`https://api.blockcypher.com/v1/btc/main/addrs/${address}/full?limit=${limit}`);
      if (!response?.ok) throw new Error('Bitcoin transactions request failed');
      
      const data = await response?.json();
      return data?.txs?.map(tx => ({
        tx_hash: tx?.hash,
        network: 'bitcoin',
        from_address: tx?.inputs?.[0]?.addresses?.[0] || 'unknown',
        to_address: tx?.outputs?.[0]?.addresses?.[0] || 'unknown',
        amount: (tx?.outputs?.[0]?.value || 0) / 100000000,
        type: this.determineTransactionType(address, tx),
        confirmations: tx?.confirmations || 0,
        block_height: tx?.block_height,
        timestamp: tx?.confirmed || tx?.received,
        transaction_fee: (tx?.fees || 0) / 100000000
      })) || [];
    } catch (error) {
      throw new Error(`Bitcoin transactions error: ${error?.message}`);
    }
  },

  determineTransactionType(address, transaction) {
    const isIncoming = transaction?.outputs?.some(output => 
      output?.addresses?.includes(address)
    );
    const isOutgoing = transaction?.inputs?.some(input => 
      input?.addresses?.includes(address)
    );
    
    if (isIncoming && !isOutgoing) return 'incoming';
    if (isOutgoing && !isIncoming) return 'outgoing';
    return 'incoming'; // Default for mixed transactions
  }
};

// Ethereum API Service
const ethereumAPI = {
  async getAddressInfo(address) {
    try {
      // Using Etherscan API for Ethereum data
      const apiKey = import.meta.env?.VITE_ETHERSCAN_API_KEY || 'YourApiKeyHere';
      const balanceResponse = await fetch(
        `https://api.etherscan.io/v2/api?chainid=1&module=account&action=balance&address=${address}&tag=latest&apikey=${apiKey}`
      );
      
      if (!balanceResponse?.ok) throw new Error('Ethereum API request failed');
      const balanceData = await balanceResponse?.json();
      const txCountResponse = await fetch(
        `https://api.etherscan.io/v2/api?chainid=1&module=proxy&action=eth_getTransactionCount&address=${address}&tag=latest&apikey=${apiKey}`
      );
      const txCountData = await txCountResponse?.json();
      
      return {
        address,
        network: 'ethereum',
        balance: parseFloat((parseInt(balanceData?.result || '0') / Math.pow(10, 18))?.toFixed(8)),
        transaction_count: parseInt(txCountData?.result || '0', 16),
        first_activity: null, // Would need additional API calls to determine
        last_activity: null
      };
    } catch (error) {
      throw new Error(`Ethereum API error: ${error?.message}`);
    }
  },

  async getAddressTransactions(address, limit = 50) {
    try {
      const apiKey = import.meta.env?.VITE_ETHERSCAN_API_KEY || 'YourApiKeyHere';
      const response = await fetch(
        `https://api.etherscan.io/v2/api?chainid=1&module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&page=1&offset=${limit}&sort=desc&apikey=${apiKey}`
      );
      
      if (!response?.ok) throw new Error('Ethereum transactions request failed');
      const data = await response?.json();
      return data?.result?.map(tx => ({
        tx_hash: tx?.hash,
        network: 'ethereum',
        from_address: tx?.from,
        to_address: tx?.to,
        amount: parseFloat((parseInt(tx?.value) / Math.pow(10, 18))?.toFixed(8)),
        type: tx?.to?.toLowerCase() === address?.toLowerCase() ? 'incoming' : 'outgoing',
        confirmations: tx?.confirmations ? parseInt(tx?.confirmations) : 0,
        block_height: parseInt(tx?.blockNumber),
        timestamp: new Date(parseInt(tx?.timeStamp) * 1000)?.toISOString(),
        gas_price: parseInt(tx?.gasPrice),
        gas_used: parseInt(tx?.gasUsed),
        transaction_fee: parseFloat((parseInt(tx?.gasUsed) * parseInt(tx?.gasPrice) / Math.pow(10, 18))?.toFixed(8))
      })) || [];
    } catch (error) {
      throw new Error(`Ethereum transactions error: ${error?.message}`);
    }
  },
};

// Scam Database Integration
const scamDatabases = {
  // Check address against known scam databases
  async checkScamStatus(address, network) {
    const results = {
      isScam: false,
      sources: [],
      riskFactors: [],
      category: 'unknown'
    };

    try {
      // Check multiple scam databases
      const checks = await Promise.allSettled([
        this.checkBitcoinAbuse(address),
        this.checkScamAlert(address),
        this.checkCryptoScamDB(address)
      ]);

      checks?.forEach((check, index) => {
        if (check?.status === 'fulfilled' && check?.value?.isScam) {
          results.isScam = true;
          results?.sources?.push(check?.value?.source);
          results?.riskFactors?.push(...check?.value?.riskFactors);
          if (check?.value?.category && results?.category === 'unknown') {
            results.category = check?.value?.category;
          }
        }
      });

      return results;
    } catch (error) {
      console.warn('Error checking scam databases:', error);
      return results;
    }
  },

  async checkBitcoinAbuse(address) {
    try {
      // Bitcoin Abuse Database simulation (replace with real API)
      const knownScamPatterns = [
        '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa', // Genesis block (for demo)
        '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy'  // Demo scam address
      ];

      if (knownScamPatterns?.includes(address)) {
        return {
          isScam: true,
          source: 'Bitcoin Abuse Database',
          category: 'darknet',
          riskFactors: [{
            title: 'Known Scam Address',
            description: 'Address reported in Bitcoin Abuse Database for fraudulent activities',
            severity: 'critical',
            impact_score: 95,
            confidence: 88,
            source: 'Bitcoin Abuse Database'
          }]
        };
      }

      return { isScam: false };
    } catch (error) {
      console.warn('Bitcoin Abuse Database check failed:', error);
      return { isScam: false };
    }
  },

  async checkScamAlert(address) {
    try {
      // Scam Alert API simulation
      const mixerPatterns = ['3J98t', 'bc1q9x30z', '1BvBMSE']; // Demo patterns
      const hasMixerPattern = mixerPatterns?.some(pattern => address?.includes(pattern));

      if (hasMixerPattern) {
        return {
          isScam: true,
          source: 'Scam Alert',
          category: 'mixer',
          riskFactors: [{
            title: 'Mixing Service Usage',
            description: 'Address associated with cryptocurrency mixing services',
            severity: 'high',
            impact_score: 75,
            confidence: 82,
            source: 'Scam Alert'
          }]
        };
      }

      return { isScam: false };
    } catch (error) {
      console.warn('Scam Alert check failed:', error);
      return { isScam: false };
    }
  },

  async checkCryptoScamDB(address) {
    try {
      // CryptoScamDB simulation
      const ransomwarePatterns = ['1A1zP1eP', '0x742d35Cc']; // Demo patterns
      const hasRansomwarePattern = ransomwarePatterns?.some(pattern => address?.includes(pattern));

      if (hasRansomwarePattern) {
        return {
          isScam: true,
          source: 'CryptoScamDB',
          category: 'criminal',
          riskFactors: [{
            title: 'Ransomware Association',
            description: 'Address linked to ransomware payment operations',
            severity: 'critical',
            impact_score: 98,
            confidence: 94,
            source: 'CryptoScamDB'
          }]
        };
      }

      return { isScam: false };
    } catch (error) {
      console.warn('CryptoScamDB check failed:', error);
      return { isScam: false };
    }
  }
};

// Risk Assessment Engine
const riskAssessment = {
  calculateRiskScore(addressData, scamData, transactionData) {
    let riskScore = 0;
    const factors = [];

    // Base risk from transaction patterns
    if (transactionData?.length > 1000) {
      riskScore += 20;
      factors?.push('High transaction volume');
    }

    // Risk from balance
    if (addressData?.balance > 100) {
      riskScore += 15;
      factors?.push('Large balance');
    }

    // Risk from scam database matches
    if (scamData?.isScam) {
      riskScore += 60;
      factors?.push(`Known scam (${scamData?.sources?.join(', ')})`);
    }

    // Risk from transaction frequency
    const recentTransactions = transactionData?.filter(tx => {
      const txDate = new Date(tx?.timestamp);
      const daysDiff = (Date.now() - txDate?.getTime()) / (1000 * 60 * 60 * 24);
      return daysDiff <= 30;
    })?.length || 0;

    if (recentTransactions > 100) {
      riskScore += 25;
      factors?.push('High recent activity');
    }

    // Risk from mixing patterns
    const hasMixingPattern = transactionData?.some(tx => 
      this.detectMixingPattern(tx, transactionData)
    );

    if (hasMixingPattern) {
      riskScore += 30;
      factors?.push('Potential mixing detected');
    }

    return {
      score: Math.min(riskScore, 100),
      factors
    };
  },

  detectMixingPattern(transaction, allTransactions) {
    // Simple mixing detection: multiple small outputs followed by consolidation
    const smallOutputs = allTransactions?.filter(tx => 
      tx?.amount < 0.1 && 
      Math.abs(new Date(tx?.timestamp)?.getTime() - new Date(transaction?.timestamp)?.getTime()) < 3600000
    );

    return smallOutputs?.length > 5;
  }
};

// Main Blockchain Service
export const blockchainService = {
  // Comprehensive address analysis
  async analyzeAddress(address, network = 'bitcoin') {
    try {
      let addressInfo, transactions, scamData;

      // Fetch basic address information
      if (network === 'bitcoin') {
        addressInfo = await bitcoinAPI?.getAddressInfo(address);
        transactions = await bitcoinAPI?.getAddressTransactions(address);
      } else if (network === 'ethereum') {
        addressInfo = await ethereumAPI?.getAddressInfo(address);
        transactions = await ethereumAPI?.getAddressTransactions(address);
      } else {
        throw new Error(`Unsupported network: ${network}`);
      }

      // Check against scam databases
      scamData = await scamDatabases?.checkScamStatus(address, network);

      // Calculate risk score
      const riskAnalysis = riskAssessment?.calculateRiskScore(addressInfo, scamData, transactions);

      // Prepare address data for database
      const addressData = {
        ...addressInfo,
        category: scamData?.category || 'unknown',
        risk_score: riskAnalysis?.score,
        is_flagged: scamData?.isScam,
        first_activity_at: addressInfo?.first_activity,
        last_activity_at: addressInfo?.last_activity
      };

      // Save to database
      const { data: savedAddress } = await addressService?.createOrUpdateAddress(addressData);

      // Save risk factors
      if (scamData?.riskFactors?.length > 0) {
        for (const factor of scamData?.riskFactors) {
          await addressService?.addRiskFactor(savedAddress?.id, factor);
        }
      }

      // Save transactions
      const savedTransactions = [];
      for (const tx of transactions?.slice(0, 20)) { // Limit to recent 20 transactions
        const { data: savedTx } = await transactionService?.createOrUpdateTransaction({
          ...tx,
          risk_score: this.calculateTransactionRiskScore(tx, scamData)
        });
        if (savedTx) savedTransactions?.push(savedTx);
      }

      return {
        address: savedAddress,
        riskFactors: scamData?.riskFactors || [],
        transactions: savedTransactions,
        riskAnalysis,
        scamData,
        error: null
      };
    } catch (error) {
      return {
        address: null,
        riskFactors: [],
        transactions: [],
        riskAnalysis: null,
        scamData: null,
        error: error?.message
      };
    }
  },

  // Calculate transaction-specific risk score
  calculateTransactionRiskScore(transaction, scamData) {
    let score = 0;

    // High amount transactions are riskier
    if (transaction?.amount > 10) score += 20;
    if (transaction?.amount > 100) score += 30;

    // Recent transactions from flagged addresses
    if (scamData?.isScam) score += 40;

    // Low confirmation count
    if (transaction?.confirmations < 3) score += 15;

    return Math.min(score, 100);
  },

  // Batch address analysis
  async analyzeBatchAddresses(addresses) {
    const results = [];
    const errors = [];

    for (const { address, network } of addresses) {
      try {
        const result = await this.analyzeAddress(address, network);
        if (result?.error) {
          errors?.push({ address, network, error: result?.error });
        } else {
          results?.push(result);
        }
      } catch (error) {
        errors?.push({ address, network, error: error?.message });
      }

      // Rate limiting - wait 1 second between requests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    return { results, errors };
  },

  // Real-time address monitoring
  async startAddressMonitoring(addresses, callback) {
    // This would implement websocket connections to blockchain APIs
    // for real-time transaction monitoring
    console.log('Starting real-time monitoring for addresses:', addresses);
    
    // Placeholder for real-time monitoring implementation
    const monitoringInterval = setInterval(async () => {
      for (const { address, network } of addresses) {
        try {
          // Check for new transactions
          let transactions = network === 'bitcoin' 
            ? await bitcoinAPI?.getAddressTransactions(address, 10)
            : await ethereumAPI?.getAddressTransactions(address, 10);

          // Notify callback of new transactions
          if (transactions?.length > 0) {
            callback?.({
              address,
              network,
              newTransactions: transactions?.slice(0, 5) // Most recent 5
            });
          }
        } catch (error) {
          console.warn(`Monitoring error for ${address}:`, error);
        }
      }
    }, 30000); // Check every 30 seconds

    // Return cleanup function
    return () => clearInterval(monitoringInterval);
  },

  async makeEthereumCall ({module, action, address}) {
    const apiKey = import.meta.env?.VITE_ETHERSCAN_API_KEY || 'YourApiKeyHere';
    const response = await fetch(`https://api.etherscan.io/v2/api?chainid=1&module=${module}&action=${action}&address=${address}&apikey=${apiKey}`)
    const data = await response.json()
    return data
  },

  // Get supported networks
  getSupportedNetworks() {
    return [
      { id: 'bitcoin', name: 'Bitcoin', symbol: 'BTC' },
      { id: 'ethereum', name: 'Ethereum', symbol: 'ETH' },
      { id: 'bitcoin_cash', name: 'Bitcoin Cash', symbol: 'BCH' },
      { id: 'litecoin', name: 'Litecoin', symbol: 'LTC' }
    ];
  }
};

export default blockchainService;